// TenantUserCountReport.jsx - Count users within each tenant (Primary Users Only)
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const TenantUserCountReport = () => {
  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;
  const isPrimary = user?.is_primary === true;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    tenant_type: "",
    include_inactive: false,
  });
  const [sortConfig, setSortConfig] = useState({
    key: "total_users",
    direction: "desc",
  });

  const loadReport = useCallback(async () => {
    if (!tenant_id || !isPrimary) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/tenant-user-count",
        {
          params: {
            tenant_id,
            tenant_type: filters.tenant_type || undefined,
            include_inactive: filters.include_inactive,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);
      }
    } catch (err) {
      console.error("Error loading tenant user count report:", err);
    } finally {
      setLoading(false);
    }
  }, [tenant_id, isPrimary, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sort the data
  const sortedData = [...reportData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "bi-arrow-down-up text-muted";
    return sortConfig.direction === "asc" ? "bi-sort-up" : "bi-sort-down";
  };

  // If not primary user, show access denied
  if (!isPrimary) {
    return (
      <div className="alert alert-warning" role="alert">
        <h4 className="alert-heading">
          <i className="bi bi-shield-exclamation me-2"></i>
          Access Restricted
        </h4>
        <p>
          The Tenant User Count Report is only available to Primary
          Administrators. This report provides cross-tenant visibility that
          requires elevated permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
        <div>
          <i className="bi bi-building me-2"></i>
          <strong>Tenant User Count Report</strong>
        </div>
        <button
          className="btn btn-sm btn-outline-light"
          onClick={loadReport}
          disabled={loading}
        >
          <i className={`bi ${loading ? "bi-arrow-repeat" : "bi-arrow-clockwise"} me-1`}></i>
          Refresh
        </button>
      </div>

      <div className="card-body">
        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-4 mb-3 mb-md-0">
            <label className="form-label small fw-bold">Tenant Type</label>
            <select
              className="form-select form-select-sm"
              value={filters.tenant_type}
              onChange={(e) =>
                setFilters({ ...filters, tenant_type: e.target.value })
              }
            >
              <option value="">All Tenant Types</option>
              <option value="vendor">Vendor</option>
              <option value="customer">Customer</option>
            </select>
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeInactive"
                checked={filters.include_inactive}
                onChange={(e) =>
                  setFilters({ ...filters, include_inactive: e.target.checked })
                }
              />
              <label className="form-check-label" htmlFor="includeInactive">
                Include Inactive Users
              </label>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="card bg-primary text-white h-100">
                <div className="card-body text-center">
                  <h3 className="mb-0">{summary.total_tenants}</h3>
                  <small>Total Tenants</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-success text-white h-100">
                <div className="card-body text-center">
                  <h3 className="mb-0">{summary.total_users}</h3>
                  <small>Total Users</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-info text-white h-100">
                <div className="card-body text-center">
                  <h3 className="mb-0">{summary.total_active_users}</h3>
                  <small>Active Users</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-secondary text-white h-100">
                <div className="card-body text-center">
                  <h3 className="mb-0">
                    {summary.avg_users_per_tenant?.toFixed(1) || "0"}
                  </h3>
                  <small>Avg Users/Tenant</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading tenant data...</p>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-building-x display-1 text-muted"></i>
            <p className="mt-3 text-muted">No tenant data found.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-light">
                <tr>
                  <th
                    onClick={() => handleSort("tenant_name")}
                    style={{ cursor: "pointer" }}
                  >
                    Tenant Name{" "}
                    <i className={`bi ${getSortIcon("tenant_name")}`}></i>
                  </th>
                  <th
                    onClick={() => handleSort("tenant_type")}
                    style={{ cursor: "pointer" }}
                  >
                    Type <i className={`bi ${getSortIcon("tenant_type")}`}></i>
                  </th>
                  <th
                    onClick={() => handleSort("total_users")}
                    style={{ cursor: "pointer" }}
                    className="text-center"
                  >
                    Total Users{" "}
                    <i className={`bi ${getSortIcon("total_users")}`}></i>
                  </th>
                  <th
                    onClick={() => handleSort("active_users")}
                    style={{ cursor: "pointer" }}
                    className="text-center"
                  >
                    Active{" "}
                    <i className={`bi ${getSortIcon("active_users")}`}></i>
                  </th>
                  <th
                    onClick={() => handleSort("inactive_users")}
                    style={{ cursor: "pointer" }}
                    className="text-center"
                  >
                    Inactive{" "}
                    <i className={`bi ${getSortIcon("inactive_users")}`}></i>
                  </th>
                  <th
                    onClick={() => handleSort("admin_count")}
                    style={{ cursor: "pointer" }}
                    className="text-center"
                  >
                    Admins{" "}
                    <i className={`bi ${getSortIcon("admin_count")}`}></i>
                  </th>
                  <th className="text-center">Roles Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row) => (
                  <tr key={row.tenant_id}>
                    <td>
                      <strong>{row.tenant_name}</strong>
                      {row.is_primary && (
                        <span className="badge bg-warning ms-2">Primary</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          row.tenant_type?.toLowerCase() === "vendor"
                            ? "bg-primary"
                            : "bg-success"
                        }`}
                      >
                        {row.tenant_type || "Unknown"}
                      </span>
                    </td>
                    <td className="text-center">
                      <strong>{row.total_users}</strong>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-success">{row.active_users}</span>
                    </td>
                    <td className="text-center">
                      {row.inactive_users > 0 ? (
                        <span className="badge bg-secondary">
                          {row.inactive_users}
                        </span>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                    <td className="text-center">
                      <span className="badge bg-danger">{row.admin_count}</span>
                    </td>
                    <td>
                      {row.roles_breakdown && row.roles_breakdown.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {row.roles_breakdown.map((role, idx) => (
                            <span
                              key={idx}
                              className="badge bg-light text-dark border"
                            >
                              {role.role}: {role.count}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan="2">
                    <strong>Totals</strong>
                  </td>
                  <td className="text-center">
                    <strong>
                      {sortedData.reduce((sum, r) => sum + (r.total_users || 0), 0)}
                    </strong>
                  </td>
                  <td className="text-center">
                    <strong>
                      {sortedData.reduce((sum, r) => sum + (r.active_users || 0), 0)}
                    </strong>
                  </td>
                  <td className="text-center">
                    <strong>
                      {sortedData.reduce((sum, r) => sum + (r.inactive_users || 0), 0)}
                    </strong>
                  </td>
                  <td className="text-center">
                    <strong>
                      {sortedData.reduce((sum, r) => sum + (r.admin_count || 0), 0)}
                    </strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Info Note */}
        <div className="alert alert-info mt-3 mb-0">
          <i className="bi bi-info-circle me-2"></i>
          <small>
            This report shows user counts across all tenants. Click column
            headers to sort. Use filters to narrow results by tenant type.
          </small>
        </div>
      </div>
    </div>
  );
};

export default TenantUserCountReport;